import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, Layers, ShieldCheck, Home, Briefcase, ArrowRight, Upload, X, CheckCircle2, AlertCircle, Loader2, Image as ImageIcon } from 'lucide-react';
import SEO from '../components/SEO';

export default function LodretStenbelaegning() {
  // Form states
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // File constraints
  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB
  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (selectedFiles.length + files.length > MAX_FILES) {
      alert(`Du kan maksimalt uploade ${MAX_FILES} billeder.`);
      return;
    }

    const validFiles = [];
    let currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`Filtypen "${file.name}" er ikke tilladt. Kun PNG, JPEG og WEBP understøttes.`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        alert(`Billedet "${file.name}" er for stort. Maksimal størrelse er 10 MB.`);
        continue;
      }

      if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
        alert(`Den samlede filstørrelse overstiger grænsen på 20 MB.`);
        break;
      }

      currentTotalSize += file.size;
      validFiles.push(file);
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Append files
    formData.delete('billeder');
    selectedFiles.forEach((file) => {
      formData.append('billeder', file);
    });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus('success');
        setSelectedFiles([]);
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage('Forbindelsesfejl. Tjek din internetforbindelse og prøv igen.');
    }
  };

  return (
    <div className="pt-24 md:pt-40 xl:pt-28 pb-16">
      <SEO
        title="Lodret Stenbelægning | Mure, Trapper & Sokler | Stenbelægning.dk"
        description="Få lodret stenbelægning til mure, sokler, trappesider og facader. Skab en elegant og sammenhængende naturstensoverflade uden fuger. Få et uforpligtende tilbud!"
      />
      {/* Hero Section */}
      <section className="bg-stone-50 py-16 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold tracking-widest uppercase text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
              Innovativ Designløsning
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-stone-900 tracking-tight mt-3">
              Få samme stenbelægning på lodrette overflader!
            </h1>
            <p className="text-xl text-stone-600 mt-4 font-medium">
              Skab et fuldendt og eksklusivt udtryk med lodret stenbelægning på mure, sokler, trapper og kanter.
            </p>
            <p className="text-stone-500 mt-4 leading-relaxed">
              Nu behøver du ikke at nøjes med smukke, fugefrie overflader på jorden. Med vores innovative lodrette stenbelægning kan du trække det samme luksuriøse naturstenslook op på mure, trappetrin, sokler og facadedetaljer. Det giver en hidtil uset arkitektonisk sammenhæng i dine udendørsarealer.
            </p>
          </div>
        </div>
      </section>

      {/* Deep Dive Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-stone-900">
                En harmonisk helhed mellem vandret og lodret
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Lodret stenbelægning er den perfekte makker til vores helstøbte stenbelægning. Ved at anvende nøjagtig samme stentyper og farveblandinger på både vandrette og lodrette flader opnår du et fuldstændig ensartet, harmonisk og moderne udtryk.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Materialet består af de samme naturlige kvarts-, granit- eller flintsten, som blandes med en specialudviklet, thixotropisk resin. Denne særlige resin sikrer, at blandingen hæfter perfekt på lodrette overflader uden at skride under udlægningen, hvilket muliggør en præcis og glat finish på selv de mest udfordrende flader.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="flex gap-3 items-start">
                  <div className="p-2 rounded-lg bg-stone-100 text-stone-900 shrink-0">
                    <Home className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900 text-sm">Til private hjem</h4>
                    <p className="text-xs text-stone-500 mt-0.5">Sokler, trapper, plantekummer og havemure.</p>
                  </div>
                </div>

                <div className="flex gap-3 items-start">
                  <div className="p-2 rounded-lg bg-stone-100 text-stone-900 shrink-0">
                    <Briefcase className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900 text-sm">Til erhverv & offentlig</h4>
                    <p className="text-xs text-stone-500 mt-0.5">Facadedetaljer, indgangspartier og mure.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Billedforslag 1: Lodret stenbelægning på trappe/sokkel */}
            <div className="space-y-4">
              <div className="relative overflow-hidden shadow-xl rounded-2xl aspect-video lg:aspect-square">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/aaacfe6e-3946-4690-a3d7-a4f1cfdfe45e.jpg"
                  alt="Trappe renoveret med lodret og vandret stenbelægning"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-3 rounded-xl border border-stone-100 shadow-lg">
                  <p className="text-xs font-bold text-stone-900">Lodret stenbelægning på trappe</p>
                  <p className="text-[10px] text-stone-500 mt-0.5">Viser den perfekte sammenhæng mellem vandrette trin og lodrette stødtrin.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hvorfor vælge lodret stenbelægning? */}
      <section className="py-16 bg-stone-50 border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-stone-900">Hvorfor vælge lodret stenbelægning?</h2>
            <p className="text-stone-500 mt-2">
              Opdag de unikke designmæssige og praktiske fordele ved at trække naturstenene op i højden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Fordel 1 */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-stone-900 text-lg">Ensartet & eksklusivt udtryk</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Skab en ubrudt visuel linje fra din indkørsel eller terrasse og op ad tilstødende mure eller sokler. Det giver et ekstremt luksuriøst og gennemtænkt arkitektonisk udtryk.
              </p>
            </div>

            {/* Fordel 2 */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Layers className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-stone-900 text-lg">Perfekt til detaljer & afslutninger</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Løsningen er ideel til fine detaljer som kanter, plantekummer, små mure, trappevanger og andre lodrette flader, hvor du ønsker at skjule træt beton eller slidt murværk.
              </p>
            </div>

            {/* Fordel 3 */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-stone-900 text-lg">Match farver & designs</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Du kan frit vælge at matche farven 1:1 med din vandrette belægning, eller skabe spændende, eksklusive kontraster ved at vælge en anden komplementær stenblanding fra Vuba.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Billedgalleri & Inspiration */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-stone-900">Inspiration & Billedforslag</h2>
            <p className="text-stone-500 mt-2">
              Se hvordan lodret stenbelægning forvandler udendørsmiljøer til eksklusive oaser.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Billedforslag 2: Detalje af stenstruktur */}
            <div className="relative overflow-hidden shadow-lg rounded-2xl aspect-video">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/d9e66450-9d19-4e2f-89b7-8625a6886679.jpg"
                alt="Nærbillede af natursten i resin"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-3 rounded-xl border border-stone-100 shadow-md">
                <p className="text-xs font-bold text-stone-900">Billedforslag: Detaljebillede af stenstruktur</p>
                <p className="text-[10px] text-stone-500 mt-0.5">Viser den tætte, skridsikre og naturlige struktur af de bundne natursten på den lodrette flade.</p>
              </div>
            </div>

            {/* Billedforslag 3: Sammenhæng mellem terrasse og lodret kant */}
            <div className="relative overflow-hidden shadow-lg rounded-2xl aspect-video">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/badd6eea-b45e-42e2-bf84-1f92e8d91be3.jpg"
                alt="Eksklusiv terrasse med fugefri belægning"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-3 rounded-xl border border-stone-100 shadow-md">
                <p className="text-xs font-bold text-stone-900">Billedforslag: Sammenhæng mellem terrasse og lodret kant</p>
                <p className="text-[10px] text-stone-500 mt-0.5">Eksklusive udendørsmiljøer med samme stenlook på både vandrette og lodrette flader.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Vusion by Vuba (Digital Visualisering) */}
      <section className="py-16 bg-stone-50 border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-emerald-600">
                <Sparkles className="h-5 w-5" />
                <span className="text-xs font-bold tracking-widest uppercase">Digital Visualisering</span>
              </div>
              <h2 className="text-3xl font-bold text-stone-900 leading-tight">
                Send et billede til os og vi forvandler det digitalt med Vusion by Vuba.
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Det kan være svært at forestille sig, hvordan en ny fugefri stenbelægning vil se ud på dine lodrette flader, mure eller sokler. Derfor tilbyder vi en professionel, digital visualisering via det avancerede **Vusion by Vuba** system.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Alt du skal gøre er at tage et godt billede af din nuværende mur, sokkel, trappe eller facade og sende det til os via formularen. Vores designere vil derefter digitalt lægge forskellige stenbelægninger, farver og designs ind på dit billede. Det gør det nemt og trygt for dig at se det færdige resultat, før du beslutter dig.
              </p>

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                  <span className="text-sm text-stone-700"><strong>100% uforpligtende:</strong> Det er helt gratis at få lavet en visualisering.</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                  <span className="text-sm text-stone-700"><strong>Se sammenhængen:</strong> Vi kan visualisere både vandrette og lodrette flader sammen.</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                  <span className="text-sm text-stone-700"><strong>Hurtigt svar:</strong> Du modtager dit visualiserede billede direkte i din indbakke.</span>
                </div>
              </div>
            </div>

            {/* Vusion Formular */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-md">
              <h3 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-emerald-600" />
                Få en gratis visualisering
              </h3>

              {status === 'success' ? (
                <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-xl text-center space-y-3">
                  <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
                  <h4 className="font-bold text-emerald-900 text-lg">Tak for din forespørgsel!</h4>
                  <p className="text-sm text-emerald-700">
                    Vi har modtaget dit billede og dine oplysninger. Vores designere går i gang med din digitale visualisering, og du vil høre fra os hurtigst muligt.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 transition-colors"
                  >
                    Send endnu et billede
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot field for anti-spam */}
                  <input
                    type="text"
                    name="website_url"
                    className="sr-only absolute"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Navn *</label>
                      <input
                        type="text"
                        name="navn"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        placeholder="Dit fulde navn"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Telefon *</label>
                      <input
                        type="tel"
                        name="telefon"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        placeholder="Dit telefonnummer"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      placeholder="din@email.dk"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Besked / Ønsker til farve</label>
                    <textarea
                      name="besked"
                      rows="3"
                      className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      placeholder="Fortæl os lidt om dit projekt og hvilke farver du overvejer (f.eks. lyse, grå eller varme sandfarver)..."
                    ></textarea>
                  </div>

                  {/* File Upload Area */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Upload billede af dit område *</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-stone-200 border-dashed rounded-xl hover:border-emerald-500 transition-colors cursor-pointer relative">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept=".png,.jpg,.jpeg,.webp"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="space-y-1 text-center">
                        <Upload className="mx-auto h-10 w-10 text-stone-400" />
                        <div className="flex text-sm text-stone-600">
                          <span className="relative font-semibold text-emerald-600 hover:text-emerald-700">Klik for at uploade</span>
                          <p className="pl-1">eller træk og slip</p>
                        </div>
                        <p className="text-xs text-stone-400">PNG, JPG, WEBP op til 10 MB pr. billede (maks 5 billeder)</p>
                      </div>
                    </div>

                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-stone-700">Valgte billeder ({selectedFiles.length}):</p>
                        <div className="grid grid-cols-1 gap-2">
                          {selectedFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg border border-stone-200 text-xs text-stone-700">
                              <span className="truncate max-w-[200px] font-medium">{file.name}</span>
                              <div className="flex items-center gap-2">
                                <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                                <button
                                  type="button"
                                  onClick={() => removeFile(idx)}
                                  className="p-1 text-stone-400 hover:text-red-500 rounded-full hover:bg-stone-100"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {status === 'error' && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5 text-xs text-red-700">
                      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting' || selectedFiles.length === 0}
                    className="w-full flex items-center justify-center px-4 py-3 rounded-xl text-sm font-bold uppercase tracking-wider text-white bg-stone-900 hover:bg-stone-800 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {status === 'submitting' ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Sender billeder...
                      </>
                    ) : (
                      'Send billeder og få visualisering'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-stone-900 text-white py-16 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold">Vil du høre mere om lodret stenbelægning?</h2>
          <p className="text-stone-400 mt-4 max-w-2xl mx-auto">
            Vi rådgiver dig gerne om, hvordan du kan kombinere lodrette og vandrette belægninger for at skabe det perfekte udtryk på din ejendom.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1 transition-all duration-300"
            >
              Få et uforpligtende tilbud
            </Link>
            <Link
              to="/vuba-materialer"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-stone-800 hover:bg-stone-700 border border-stone-700 hover:-translate-y-1 transition-all duration-300"
            >
              Se farver og materialer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
